import "chart.js/auto";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { PiArrowUpFill } from "react-icons/pi";
import { useNavigate } from "react-router";
import { useQuery } from "react-query";
import { getUserMetrics, getUsers } from "../../api/user";
import { Input, Table } from "antd";
import { columns } from "./column";

function Customers() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // debounce search so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching } = useQuery(
    ["customers", page, pageSize, search],
    () => getUsers({ page, limit: pageSize, search }),
    { keepPreviousData: true },
  );
  const { data: metricsData } = useQuery("customers-metrics", getUserMetrics);  

  const navigate = useNavigate();

  return (
    <div className="w-full h-full pt-5 px-5 pb-40 space-y-6">
      <div className="flex flex-col gap-5">
        <div className="font-dmsans font-bold text-[22px] text-[#070E05]">
          Customers
        </div>
        <div className="flex gap-5">
          <div className="w-1/2 bg-white rounded-lg p-4 gap-4 flex flex-col h-fit">
            <div className="chart-heading flex justify-between items-center font-dmsans">
              <div className="text-[#181819E5] font-bold text-[14px]">
                Customer Growth
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#2121F1]"></div>
                  <div className="text-[#1818196B] font-medium text-[12px]">
                    New Customers
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#F0D961]"></div>
                  <div className="text-[#1818196B] font-medium text-[12px]">
                    Returning Customers
                  </div>
                </div>
              </div>
            </div>
            <CustomerChart series={metricsData?.series} />
          </div>
          <div className="w-1/2">
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(48%, 0fr))",
              }}
            >
              <CustomersItem
                data={{
                  title: "Total Customers",
                  amount: metricsData?.cards?.totalCustomers || 0,
                  increment: "0%",
                }}
              />
              <CustomersItem
                data={{
                  title: "New Customers",
                  amount: metricsData?.cards?.newCustomers || 0,
                  increment: "0%",
                }}
              />
              <CustomersItem
                data={{
                  title: "Returning Customers",
                  amount: metricsData?.cards?.returningCustomers || 0,
                  increment: "0%",
                }}
              />
              <CustomersItem
                data={{
                  title: "Conversion Rate",
                  amount: metricsData?.cards?.conversionRate || 0,
                  increment: "0%",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 bg-white rounded-lg p-5">
        <div className="flex justify-between items-center gap-3">
          <div className="tracking-[-0.4 px] font-dmsans font-bold text-[18px] text-[#070E05]">
            All Customers
          </div>
          <Input.Search
            allowClear
            placeholder="Name, email or phone number"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-[300px]"
          />
        </div>
        <div className="table-container w-full">
          <Table
            rowKey="_id"
            loading={isFetching}
            dataSource={data?.customers || []}
            columns={columns(navigate)}
            pagination={{
              current: page,
              pageSize,
              total: data?.total || 0,
              showSizeChanger: true,
              onChange: (newPage, newSize) => {
                if (newSize !== pageSize) {
                  setPageSize(newSize);
                  setPage(1);
                } else {
                  setPage(newPage);
                }
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Customers;

function CustomersItem(props) {
  return (
    <div className="w-full bg-white font-dmsans  rounded-lg px-[28px] py-[18px]">
      <div className="flex flex-col gap-3">
        <div className="text-[12px] text-[#00000080]">{props.data.title}</div>
        <div className="text-[26px] font-bold text-[#14142B]">
          {props.data.amount}
        </div>
        <div className=" bg-[#27AE601A] text-[#27AE60] text-[13px] w-fit rounded-[3px] px-[3px] flex items-center gap-1">
          <PiArrowUpFill />
          {props.data.increment}
        </div>
      </div>
    </div>
  );
}

export function CustomerChart({ series }) {
  const options = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          lineWidth: 0.5,
          tickBorderDash: [1, 10],
        },
        border: {
          dash: [50, 10],
        },
        ticks: {
          align: "end",
          display: true,
          font: {
            size: 11,
          },
          color: "#1818196B",
        },
      },
      y: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          font: {
            size: 11,
          },
          callback: function (val, index) {
            if (index == 0) {
              return 0;
            } else if (index == 3) {
              return 25;
            } else if (index == 6) {
              return 50;
            } else if (index == 9) {
              return 100;
            }
          },
        },
      },
    },
    maintainAspectRatio: false,
  };
  return (
    <div className="bar-container">
      <Line
        data={{
          labels: series?.map((ser) => ser.label) || [],
          datasets: [
            {
              label: "",
              data: series?.map((ser) => ser.newCustomers).splice(0, 13) || [],
              borderColor: "#2121F1",
              backgroundColor: "transparent",
              fill: false,
              tension: 0.5,
              pointStyle: false,
              borderWidth: 2,
            },
            {
              label: "",
              data:
                series?.map((ser) => ser.returningCustomers).splice(0, 13) ||
                [],
              borderColor: "#F0D961",
              backgroundColor: "transparent",
              fill: false,
              tension: 0.5,
              pointStyle: false,
              borderWidth: 2,
            },
          ],
        }}
        options={options}
        width={window.innerWidth}
        height={300}
      />
      <div
        className="chart-text"
        style={{
          color: "gray",
          fontSize: "13px",
        }}
      >
        {false ? <div>No data for this chart</div> : <></>}
      </div>
    </div>
  );
}
